import React, { Component } from 'react';
import { WatchlistListsModel, WatchlistModel } from '../../../domain/watchlist/WatchlistDomains';
import Icon from '../../../icons/Icon';
import LanguageUtil from '../../../util/LanguageUtil';
import WatchlistUtil from '../../../util/WatchlistUtil';
import WatchlistCustomListSourceLoader, { CustomListSourceDisplay } from '../../../util/WatchlistCustomListSourceLoader';

class State {
  public customListSources: Record<string, CustomListSourceDisplay> = {};
}

class WatchlistSourcesComponent extends Component<Props, State> {
  public state: State = new State();

  public render() {
    if (this.getSources() == undefined || this.getSources().length == 0) {
      return;
    }
    return (
      <div>
        <div className={'bg-[#F2F4F7] w-full h-full rounded py-2 px-4 mt-10'}>
          <span className={'text-f-black-2 text-md'}>{LanguageUtil.get({ id: 'common.label.sources', defaultMessage: 'Sources' })}</span>
        </div>
        <div className={'h-full w-full pt-2'}>
          {this.getSources().map((data, index) => {
            return (
              <div className={'flex gap-x-2 p-2 border-b border-b-[0.5px]'} key={index}>
                <div className={'w-[20px]'}>
                  <Icon icon={'kyc_sourcesicon'} color={'#607080'} size={16} />
                </div>
                <div className={'w-full h-full flex justify-between gap-y-1 text-md text-[11px] text-[#607080]'}>
                  <div className={'flex flex-col gap-y-1'}>
                    <span className={'text-f-black-2'}>{this.getName(data.watchlistId)}</span>
                    {!this.isCustomList(data.watchlistId) && (
                      <span
                        className={'text-[#458EF7] cursor-pointer'}
                        onClick={() => window.open(this.getList(data.watchlistId)?.authorityWebsite, '_blank')}
                      >
                        {this.getList(data.watchlistId)?.authority}
                      </span>
                    )}
                  </div>
                  <div className={'flex gap-x-2 items-center'}>
                    <span className={'flex items-center gap-x-1 text-md min-w-[88px] justify-center text-[#5925DC] bg-[#F4F3FF] rounded-xl px-[8px] py-[3px]'}>
                      <Icon icon={'kyc_namesicon'} size={16} />
                      {this.getSize(data.watchlistId)}
                    </span>
                    {this.isCustomList(data.watchlistId) ? (
                      <img src={'/icons/custom-list.png'} className={'w-[20px] h-[20px]'} alt='custom-list' />
                    ) : (
                      <>
                        <span className={'flex text-md min-w-[88px] justify-center text-[#4F4F4F] bg-[#F2F4F7] rounded-xl px-[8px] py-[3px]'}>
                          {this.getList(data.watchlistId)?.country}
                        </span>
                        <img src={`/country/${this.getList(data.watchlistId)?.country}.svg`} className={'w-[20px] h-[20px]'} alt='country' />
                        <Icon icon={'link'} size={16} color={'#458EF7'} className={'cursor-pointer'} onClick={() => window.open(data.source, '_blank')} />
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  public async componentDidMount(): Promise<void> {
    await this.loadCustomListSources();
  }

  public async componentDidUpdate(prevProps: Props): Promise<void> {
    if (this.buildLoadKey(prevProps) !== this.buildLoadKey(this.props)) {
      await this.loadCustomListSources();
    }
  }

  private getSources = (): SourceItem[] => {
    const { selectedResponse } = this.props;
    return (selectedResponse || []).map((value: WatchlistModel) => ({
      source: value.watchlistRecord.source,
      watchlistId: value.watchlistId,
    }));
  };

  private getName = (listId: string): string | undefined => {
    const customListSource: CustomListSourceDisplay | undefined = this.state.customListSources[listId];
    if (customListSource != undefined) {
      return customListSource.name;
    }
    if (this.props.preferredCustomListSource != undefined && this.isCustomListCandidate(listId)) {
      return this.props.preferredCustomListSource.name;
    }
    return this.getList(listId)?.name;
  };

  private getSize = (listId: string): number | undefined => {
    const customListSource: CustomListSourceDisplay | undefined = this.state.customListSources[listId];
    if (customListSource != undefined) {
      return customListSource.size;
    }
    if (this.props.preferredCustomListSource != undefined && this.isCustomListCandidate(listId)) {
      return this.props.preferredCustomListSource.size;
    }
    return this.getList(listId)?.size;
  };

  private isCustomList = (listId: string): boolean => {
    if (this.state.customListSources[listId] != undefined) {
      return true;
    }
    if (this.props.preferredCustomListSource != undefined && this.isCustomListCandidate(listId)) {
      return true;
    }
    return WatchlistUtil.isCustomList(this.getList(listId));
  };

  private isCustomListCandidate = (listId: string): boolean => {
    const engineList: WatchlistListsModel | undefined = this.getList(listId);
    if (WatchlistUtil.isCustomList(engineList)) {
      return true;
    }
    return engineList == undefined && WatchlistUtil.getCustomListIdFromPath(window.location.pathname) != undefined;
  };

  private getList = (listId: string): WatchlistListsModel | undefined => {
    const { watchlistResponse } = this.props;
    if (watchlistResponse && watchlistResponse.length > 0) {
      return watchlistResponse.find((list) => list.id == listId);
    }
    return undefined;
  };

  private buildLoadKey = (props: Props): string => {
    const watchlistIds: string = (props.selectedResponse || []).map((item: WatchlistModel) => item.watchlistId).join('|');
    const listIds: string = (props.watchlistResponse || []).map((list: WatchlistListsModel) => list.id).join('|');
    const preferred: string = props.preferredCustomListSource ? `${props.preferredCustomListSource.name}:${props.preferredCustomListSource.size}` : '';
    return `${watchlistIds}::${listIds}::${preferred}`;
  };

  private loadCustomListSources = async (): Promise<void> => {
    const customListSources: Record<string, CustomListSourceDisplay> = await WatchlistCustomListSourceLoader.load(
      this.getSources().map((source: SourceItem) => source.watchlistId),
      this.props.watchlistResponse || [],
      this.props.preferredCustomListSource,
    );
    this.setState({ customListSources });
  };
}

interface SourceItem {
  source: string | undefined;
  watchlistId: string;
}

interface Props {
  selectedResponse: WatchlistModel[];
  watchlistResponse: WatchlistListsModel[];
  preferredCustomListSource?: CustomListSourceDisplay;
}

export default WatchlistSourcesComponent;
